import createUid from './util/uid';

/** Parent class for chart overlay events (dividends, earnings etc.) */
class Event {
	/** @param {Object} args - Custom event args */
	constructor(args) {
		/** @type {Panel} */
		this.panel = args.panel;
		/** @type {object} */
		this.params = this._getParams(args.params);
		/** @type {object[]} */
		this.coords = [];
	}

	/**
	 * A wrapper for getDefaultParams. Merges default params with
	 * developer-provided params.
	 * @param {object} args - Custom event args
	 * @returns {object} Combined default and developer-provided params.
	 */
	_getParams(params) {
		return {
			...this.getDefaultParams(),
			style: this.panel.core.getStyle('event'),
			uid: createUid(),
			visible: true, // Whether or not the event should be rendered
			...params
		};
	}

	/**
	 * Gets the x/y coordinates for each event
	 * @param {object} data - Symbol data
	 * @param {string} eventType - Specific event type
	 * @returns {object[]} Normalized array of event coordinates with data
	 */
	getPositions(data, eventType) {
		if (!data[eventType] || !data.price) return [];

		const coords = [];
		let date;
		let i = 0;
		let x = 0;
		let y = 0;
		let closestPriceIdx = 0;
		let closestExchangeIdx = 0;
		let dataPrice = [];
		let dataSeries = 0;
		let customValue = false;
		const dataEvent = data[eventType];
		const isIntraday = this.panel.core.state.isIntraday;
		const isOHLC = /candlestick|ohlc|hlc/.test(
			this.panel.indicators[0].params.markerType
		);
		const yOHLC = isOHLC ? 'high' : 'close';

		for (const uid in dataEvent || {}) {
			if (Object.prototype.hasOwnProperty.call(dataEvent, uid)) {
				dataSeries = dataEvent[uid];

				for (i = 0; i < dataSeries.length; i++) {
					if (!dataSeries[i]) continue;

					customValue = typeof dataSeries[i].value === 'number';

					let dateIn = dataSeries[i].date;

					// discard time portion of interday dates
					if (!isIntraday && typeof dateIn === 'string') {
						dateIn = dateIn.replace(/T[\d:Z]+$/, '');
					}

					// Enforce zulu time for cross-browser compatibility
					if (typeof dateIn === 'string' && /T.+[^Z]$/.test(dateIn)) {
						dateIn = `${dateIn}Z`;
					}

					date = new Date(Date.parse(dateIn));

					if (eventType === 'custom') {
						// offset date to local tz
						date.setMinutes(
							date.getMinutes() + date.getTimezoneOffset()
						);
					}

					closestExchangeIdx = this.panel.core.closestExchangeIndex(
						date
					);

					if (customValue) {
						x = this.panel.xAxis.primaryScale(closestExchangeIdx);
						y = this.panel.yAxis.primaryScale(dataSeries[i].value);
					} else {
						if (date > new Date()) {
							x = -1;
						} else {
							dataPrice = this.getFirstDataset(data.price);

							closestPriceIdx = this.panel.core._getIndexByDate(
								dataPrice,
								date
							);

							x = this.panel.xAxis.primaryScale(closestExchangeIdx);
							y = this.panel.yAxis.primaryScale(
								dataPrice[closestPriceIdx][yOHLC]
							);
						}
					}

					// must fall within panel area
					if (
						x > this.panel.size.padding.left - this.params.style.canvasBufferWidth.left &&
						x < this.panel.size.padding.left + this.panel.size.width + this.params.style.canvasBufferWidth.right
					) {
						coords.push({
							x,
							y,
							event: dataSeries[i],
							uid: this.params.uid
						});
					}
				}
			}
		}

		return this.getStackPositions(coords);
	}

	/**
	 * Updates coordinate positions to stack events vertically that are close
	 * in horizontal proximity
	 * @param {object[]} coords - array of event coordinates and data
	 * @returns {object[]} array of updated event coordinates and data
	 * @private
	 */
	getStackPositions(coords) {
		const { style } = this.params;
		// The horizontal distance between event icons that will trigger stacking
		const clusterDist = this.params.clusterDist || style.cluster.distance || 15;
		// The vertical distance between stacked event icons
		const stackDist = this.params.stackDist || style.stack.distance || 17;
		let prev = {};
		let curr = {};
		let alt = {};

		// check for collisions with other event groups
		for (let i = coords.length - 1; i >= 0; i--) {
			curr = coords[i];

			for (const altEvent of this.panel.events) {
				if (
					altEvent !== this &&
					altEvent.coords &&
					!altEvent.isHidden
				) {
					for (let a = altEvent.coords.length - 1; a >= 0; a--) {
						alt = altEvent.coords[a];

						if (Math.abs(alt.x - curr.x) < clusterDist) {
							// stack curr event on top of alt
							curr.y = alt.y - stackDist;

							// snap curr x to alt
							curr.x = alt.x;
						}
					}
				}
			}
		}

		// check for collisions with neighbor events in the same event group
		for (let i = coords.length - 1; i >= 0; i--) {
			prev = coords[i - 1];
			curr = coords[i];

			// check left-hand neighbor in current event group
			if (prev && Math.abs(prev.x - curr.x) < clusterDist) {
				// stack prev on curr
				prev.y = curr.y - stackDist;

				// snap prev x to curr x
				prev.x = curr.x;
			}
		}

		return coords;
	}

	/**
	 * Returns the first available dataset
	 * @param {object} data - symbol data
	 * @return {object[]}
	 * @private
	 */
	getFirstDataset(data) {
		for (const x in data) {
			if (Object.prototype.hasOwnProperty.call(data, x)) {
				return data[x];
			}
		}
	}

	/**
	 * Render default event flags if none overridden locally. Called in sub-class
	 * defined render function with default params for that sub-class.
	 * @param {object} data - symbol data
	 * @param {string} eventType - the event type name
	 * @param {string} fillColor - the hex color fill
	 * @param {string} label - the event label to be rendered
	 * @private
	 */
	renderDefault(data, eventType, fillColor, label) {
		const { core } = this.panel;
		// if a render listener has been registered, emit and return
		if (core.eventEmitter.listeners('EVENT_RENDER').length > 0) {
			return core.eventEmitter.emit('EVENT_RENDER', {
				id: eventType,
				name: this.params.name,
				coords: this.coords,
				ctx: this.panel.rootContext,
				event: this
			});
		}

		// draw default icon
		const { coords } = this;
		const ctx = this.panel.rootContext;
		const { diameter } = this.params.style;
		const radius = diameter / 2;
		const padding = 5;
		const offsetY = 20;

		ctx.fillStyle = fillColor;
		ctx.textBaseline = 'top';

		// pointer shape
		coords.forEach(c => {
			const x = c.x;
			const y = c.y - offsetY;
			ctx.beginPath();
			ctx.arc(x, y, radius, 0, 2 * Math.PI);
			ctx.moveTo(x + radius, y + 1);
			ctx.lineTo(x, y + diameter);
			ctx.lineTo(x - radius, y + 1);
			ctx.fill();
		});

		ctx.beginPath();

		// label
		ctx.fillStyle = '#222222';
		ctx.font = 'bold 14px Arial';
		coords.forEach(c => {
			const x = c.x;
			const y = c.y - offsetY;
			ctx.fillText(label, x - 5, y - 5.5);
		});

		// metadata
		coords.forEach(c => {
			const x = c.x;
			const y = c.y - offsetY;
			c.id = this.params.id;
			c.left = x - (radius + padding);
			c.top = y - (radius + padding);
			c.right = c.left + diameter + 2 * padding;
			c.bottom = c.top + diameter + 2 * padding;
			// debug hitbox
			//ctx.rect(coords[i].left, coords[i].top, coords[i].right - coords[i].left, coords[i].bottom - coords[i].top);
		});

		ctx.stroke();
		ctx.closePath();
	}
	/**
	 * Render function must be implemented in sub-class
	 * @abstract
	 */
	render() {
		throw new Error('Required Event method not found');
	}

	/**
	 * Remove function must be implemented in sub-class
	 * @abstract
	 */
	remove() {
		return;
	}

	/**
	 * Checks if mouse is within coordinates of one of the currently-visible events
	 * @param {number[]} mouse - mouse coordinates
	 * @return {boolean}
	 */
	isWithin([x, y]) {
		// Adjust Y coord for top margin. This is necessary because event y coords are saved relative to the zero value of the scale.
		y -= this.panel.size.margin.top;

		let response = null;
		const coords = this.coords || {};

		for (const key in coords) {
			const eventHitbox = coords[key];

			// Check to see if the mouse is within the event hitbox
			if (
				x > eventHitbox.left &&
				y > eventHitbox.top &&
				x < eventHitbox.right &&
				y < eventHitbox.bottom
			) {
				response = eventHitbox;
				break;
			}
		}

		return response;
	}

	/**
	 * Returns the default params for this type of Event. Must be implemented in sub-class
	 * @abstract
	 * @private
	 */
	getDefaultParams() {
		throw new Error('Required Event method not found');
	}
}

export default Event;
